import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { BrandConfig } from '../lib/types'
import { ACTIVE_BRAND_KEY } from './useBrand'
import {
  type Account, type AgencyBrand, type AccountData, type AccountRole,
  accountFromRow, agencyBrandFromConfig, soloAccountFromBrand,
} from '../lib/agency'
import { onAppRevalidate } from '../lib/revalidate'
import { readBrandAccess, findAccess } from '../lib/brandAccess'

// The account layer that sits above brands (spec §2). Resolves the user's
// account + its brands from the anchor brand's account_id:
//   brand_configs.account_id → accounts → brand_configs where account_id = …
// A solo 'brand' account has exactly one brand; an 'agency' account has many.
//
// activeBrandId feeds useEffectiveBrand (see useBrand.ts), which swaps
// BrandContext.brand to the active brand so the whole dashboard reads that
// brand's data. If no accounts row resolves yet (a brand created before the
// backfill, or the brief rollout window), it falls back to a solo account
// derived from the brand — real state, never a mock.

interface AccountContextType {
  account: Account | null
  brands: AgencyBrand[]
  isAgency: boolean
  // True when the current user owns the account (account.owner_user_id === user).
  // The reliable signal for gating billing/agency actions (add brand, $997
  // upgrade) — works even for a brandless agency, where the brand-level isOwner
  // would be false because there's no brand_user_access row yet.
  isAccountOwner: boolean
  // The caller's own account_members.role ('owner' | 'admin' | 'member'), or null
  // when they have no account membership (a brand-scoped member on the solo
  // fallback). Distinct from isAccountOwner: an agency 'admin' manages the team
  // and brands but not billing, so team UI gates on isAccountAdmin, not ownership.
  // EVERY account the user belongs to. More than one means they were invited to
  // multiple agencies; drives the account switcher. Always includes `account`.
  accounts: Account[]
  // Switch which account is active: persists the choice, drops the previous
  // account's brand selection, and hard-reloads so no data from the old account
  // lingers (same contract as useBrand.switchBrand).
  switchAccount: (accountId: string) => void
  // Brands the user can work in that belong to no account they're a member of
  // (a solo brand that added them to its team). Listed alongside `accounts` in
  // the switcher; without this they have no way in.
  standaloneBrands: AgencyBrand[]
  // Enter one of those brands as its own workspace.
  switchBrandWorkspace: (brandId: string) => void
  // What the switcher should mark as current. Null until the first load settles.
  activeWorkspace: Workspace | null
  accountRole: AccountRole | null
  // owner OR co-owner OR admin — may manage the agency team and its brands.
  // Mirrors the worker's ACCOUNT_MANAGER_ROLES gate in agency_team.ts.
  isAccountAdmin: boolean
  // The above PLUS viewer — may LOOK at the agency's management surface: the
  // team roster and the activity log. Mirrors ACCOUNT_READER_ROLES. Route and
  // nav visibility gate on this; every button that changes something gates on
  // isAccountAdmin, and the worker refuses a viewer regardless.
  canViewAgency: boolean
  // owner OR co-owner — may touch the money and the agency's identity: billing,
  // the subscription, agency Settings. Mirrors the worker's accountPrincipal()
  // (ACCOUNT_PRINCIPAL_ROLES), which is what those endpoints actually enforce.
  //
  // Distinct from isAccountOwner, which is ownership of the accounts row and
  // stays the test for anything only ONE person may do. Billing and settings are
  // gated on this instead, because that is exactly what the co-owner role adds.
  canManageAccount: boolean
  activeBrandId: string | null
  setActiveBrand: (id: string) => void
  // Whether the agency is currently "inside" a brand (vs. at the agency lobby).
  // Drives whether "/" shows the brand dashboard or redirects to /agency.
  inBrand: boolean
  // Verifies the access still exists first; false = refused, don't navigate.
  enterBrand: (id: string) => Promise<boolean>
  exitToAgency: () => void           // back to the lobby
  refreshAccount: () => void         // reload account + brands (after add-brand)
  // A brand whose access was found to be gone, for the lobby to explain.
  accessRevoked: { id: string; name: string } | null
  noteAccessRevoked: (b: { id: string; name: string }) => void
  clearAccessRevoked: () => void
  loading: boolean
}

const noop = () => {}
export const AccountContext = createContext<AccountContextType>({
  account: null, brands: [], accounts: [], switchAccount: noop,
  standaloneBrands: [], switchBrandWorkspace: noop, activeWorkspace: null,
  isAgency: false, isAccountOwner: false, accountRole: null,
  isAccountAdmin: false, canViewAgency: false, canManageAccount: false, activeBrandId: null, setActiveBrand: noop,
  inBrand: false, enterBrand: async () => false, exitToAgency: noop, refreshAccount: noop,
  accessRevoked: null, noteAccessRevoked: noop, clearAccessRevoked: noop, loading: true,
})

export function useAccount() {
  return useContext(AccountContext)
}

const ACTIVE_KEY = 'bmc_active_brand'
const IN_BRAND_KEY = 'bmc_in_brand'
// Which ACCOUNT the user is currently working in. Only meaningful for someone
// who belongs to more than one (invited to two agencies, say) — pickAccount
// otherwise guesses, and there was no way to tell it which one you meant.
const ACTIVE_ACCOUNT_KEY = 'bmc_active_account_id'

// The roster in front of someone who left a tab open for an hour is an hour
// old; this is how stale it may get before a tab return re-checks access.
const ACCOUNT_REVALIDATE_MS = 60_000

/** A brand row as read for the switcher/roster: light fields + its account. */
interface MyBrandRow {
  id: string
  brand_name: string
  logo_url: string | null
  category: string | null
  account_id: string | null
}

/** Which workspace is currently resolved — an account, or a standalone brand. */
export type Workspace = { kind: 'account' | 'brand'; id: string }

// Wipe ALL persisted active-brand selection: the agency active-brand id
// (ACTIVE_KEY), the "inside a brand" flag (IN_BRAND_KEY), and the legacy
// multi-brand switcher key (ACTIVE_BRAND_KEY, owned by useBrand). Call on
// sign-out (so the next user in this browser doesn't inherit a stale selection)
// and when creating a brand-new agency (which has no brands and must start at
// the lobby). A stale selection otherwise leaves `inBrand=true` and makes
// DefaultRedirect fall through to Home — a blank brand dashboard — instead of
// routing to the agency lobby.
export function clearActiveBrandSelection(): void {
  try {
    localStorage.removeItem(ACTIVE_KEY)
    localStorage.removeItem(ACTIVE_BRAND_KEY)
    sessionStorage.removeItem(IN_BRAND_KEY)
  } catch { /* storage disabled */ }
}

/** Also forget which ACCOUNT was active. Sign-out only — switching accounts sets
 *  a new value rather than clearing it. */
export function clearActiveAccountSelection(): void {
  try { localStorage.removeItem(ACTIVE_ACCOUNT_KEY) } catch { /* storage disabled */ }
}

/** Which WORKSPACE the user picked in the switcher.
 *
 *  Usually an account id. It can also be `brand:<id>`, meaning "a brand I have
 *  direct access to that belongs to no account I'm a member of" — a solo brand
 *  that added me to its own Team page. Those brands are invisible to account
 *  resolution (which only ever looks at account_members), so without an explicit
 *  marker there is no way to ask for one. */
function readWorkspaceChoice(): { kind: 'account' | 'brand'; id: string } | null {
  let raw: string | null = null
  try { raw = localStorage.getItem(ACTIVE_ACCOUNT_KEY) } catch { return null }
  if (!raw) return null
  return raw.startsWith('brand:')
    ? { kind: 'brand', id: raw.slice('brand:'.length) }
    : { kind: 'account', id: raw }
}

/** A Whop agency checkout just returned; activation is mid-flight. Nothing may
 *  redirect resolution away from the agency account while this is true. */
function isAgencyCheckoutReturn(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('agency') === '1'
  } catch { return false }   // no window (SSR/tests)
}

// Choose which account a user belongs to when more than one row comes back.
//
// A user should have exactly ONE account, but both directions of the step-1
// Brand/Agency toggle can strand a second, never-activated one:
//   - Brand→Agency mid-onboarding used to leave a stray solo 'brand' account.
//   - Agency→Brand leaves a stray brandless 'agency' account: /api/agency/init
//     creates the account BEFORE the $997 checkout, and if the user abandons it
//     and signs up as a brand instead, /api/brand/onboard creates a SECOND
//     account (its idempotency check looks for an existing brand, not an
//     existing account).
//
// Picking wrong is not cosmetic. Resolving a stray AGENCY account makes
// AuthenticatedApp read THAT account's onboarding_completed/whop_subscription_id
// and drop a fully-paid brand owner back into the signup wizard, with no way out
// (the stray agency has no brands to enter). This previously preferred
// type='agency' unconditionally, which is exactly that failure.
//
// Rank by evidence of real use, most decisive first:
//   0. an agency checkout return in the URL (?agency=1) — the $997 sub hasn't
//      landed yet, so nothing else identifies the agency as the live account;
//      CheckoutActivation polls it and must not be pointed at a brand account.
//   1. the account carrying a subscription — the one actually being paid for.
//   2. the account that anchors the user's current brand (brand.account_id).
//      Correct for real agencies too: their brands carry the agency's account_id.
//   3. an agency over a brand account — brandless-agency signup, where there is
//      no brand to anchor on.
//   4. first row.
export function pickAccount(rows: any[], brand: BrandConfig | null): any {
  if (!rows.length) return null
  const agency = rows.find(a => a?.type === 'agency')
  if (isAgencyCheckoutReturn() && agency) return agency
  // An explicit pick from the account switcher outranks everything below — those
  // branches exist to GUESS when the user hasn't told us which account they mean.
  // Ignored when it no longer matches a row (removed from that agency, say), so a
  // stale value can never strand them.
  // A `brand:` marker is not an account — it's handled before this is ever
  // called, and must fall through to the heuristics if it somehow reaches here.
  const ws = readWorkspaceChoice()
  if (ws?.kind === 'account') {
    const picked = rows.find(a => a?.id === ws.id)
    if (picked) return picked
  }
  return rows.find(a => !!a?.whop_subscription_id)
    ?? (brand?.account_id ? rows.find(a => a?.id === brand.account_id) : undefined)
    ?? agency
    ?? rows[0]
}

export function useAccountLoader(
  brand: BrandConfig | null,
  userId: string | undefined,
  brandLoading: boolean,
): AccountContextType {
  const [data, setData] = useState<AccountData | null>(null)
  const [myAccountRole, setMyAccountRole] = useState<AccountRole | null>(null)
  const [allAccounts, setAllAccounts] = useState<Account[]>([])
  const [standaloneBrands, setStandaloneBrands] = useState<AgencyBrand[]>([])
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  // Set when access to a brand turns out to be gone — on a refused entry, or on
  // finding the brand you're INSIDE no longer accessible. Survives the reload it
  // triggers (that only bumps reloadKey), so the lobby can explain why the card
  // disappeared instead of leaving the user wondering.
  const [accessRevoked, setAccessRevoked] = useState<{ id: string; name: string } | null>(null)
  const refreshAccount = useCallback(() => setReloadKey(k => k + 1), [])
  const clearAccessRevoked = useCallback(() => setAccessRevoked(null), [])

  // Show the full-page loader only on a real identity change (login / brand
  // swap) — NOT on refreshAccount (reloadKey), so the agency-activation poll and
  // post-add-brand refresh reload silently instead of flickering the loader.
  const idKey = `${userId ?? ''}|${brand?.id ?? ''}|${brand?.account_id ?? ''}`
  const lastIdKeyRef = useRef<string | null>(null)
  // Brand names, kept in a ref so a refused entry can NAME the brand in its
  // message after refreshAccount has already dropped it from `brands`.
  const brandNameRef = useRef<Map<string, string>>(new Map())

  useEffect(() => {
    let cancelled = false
    if (!userId) { setData(null); setLoading(false); lastIdKeyRef.current = idKey; return }
    // Hold until the brand loader settles. Account resolution below falls back
    // to brand?.account_id, so running it while brand is still `null` mid-fetch
    // resolves a premature "no brand yet" account and flips loading false — then
    // the real brand arrives, brand?.id/account_id change, and this effect reruns
    // and resolves again. That used to paint AuthenticatedApp's dashboard for one
    // wrong frame in between (both loading flags false), then flip back to the
    // loading screen before the real repaint. Waiting here removes that gap.
    if (brandLoading) { setLoading(true); return }
    if (lastIdKeyRef.current !== idKey) setLoading(true)
    lastIdKeyRef.current = idKey
    ;(async () => {
      // Resolve the account from MEMBERSHIP first — this works even with zero
      // brands (a fresh agency). Brand-scoped team members have no account_members
      // row, so they fall through to the brand-based path below.
      //
      // A user normally belongs to exactly ONE account; abandoned signup paths
      // can leave a second one behind (see pickAccount).
      //
      // memberRows and accessRows (below) are two independent per-user reads —
      // neither's query depends on the other's result — so both fire at once
      // instead of accessRows waiting for memberRows, and everything chained
      // off memberRows, to finish first.
      //
      // Reading brand_user_access (accessRows) is what makes the brand list
      // trustworthy: its SELECT policy is correctly self-scoped
      // (user_id = auth.uid()), whereas brand_configs is readable by ANY
      // authenticated user — "creators_read_brand_configs" (20260501000006)
      // added `FOR SELECT TO authenticated USING (true)` for the creator app's
      // job cards, and RLS policies are OR'd, so that blanket policy beats the
      // restrictive "brand_configs_select". Querying brand_configs alone would
      // hand this user brands they have no access to.
      // readBrandAccess is the SAME read useBrandLoader just made (see
      // lib/brandAccess.ts) — on a first load it is served from that, turning
      // what were two sequential round trips into one. `force` on a refresh
      // (reloadKey > 0), because the tab-return revalidation exists precisely to
      // notice revoked access and must never be answered from cache.
      const [{ data: memberRows }, { rows: accessRows }] = await Promise.all([
        supabase.from('account_members').select('account_id, role').eq('user_id', userId),
        readBrandAccess(userId, { force: reloadKey > 0 }),
      ])
      if (cancelled) return
      const memberships = (memberRows ?? []) as { account_id: string; role: string }[]
      const memberIds = memberships.map(r => r.account_id).filter(Boolean)

      // Every account the user belongs to. Resolved BEFORE anything else so the
      // switcher can list them even when we end up in a brand workspace below.
      let acctRows: any[] = []
      if (memberIds.length) {
        const { data } = await supabase
          .from('accounts').select('*').in('id', memberIds)
        if (cancelled) return
        acctRows = data ?? []
        // Keep the FULL list, not just the pick — the switcher needs every
        // account this user can reach.
        setAllAccounts(acctRows.map(accountFromRow))
      } else {
        setAllAccounts([])
      }

      // The user's own accessible brands, with the account each belongs to. One
      // read (accessRows, fetched above) serving two jobs below: scoping an
      // account's roster to what this user can actually reach, and finding
      // brands that belong to NO account they're a member of.
      const accessibleIds = accessRows.map(r => r.brand_id)
      const { data: myBrandRows } = accessibleIds.length
        ? await supabase
            .from('brand_configs')
            .select('id,brand_name,logo_url,category,account_id')
            .in('id', accessibleIds)
            .neq('is_active', false)   // exclude brands removed/archived from an agency
            .order('created_at', { ascending: true })
        : { data: [] as MyBrandRow[] }
      if (cancelled) return
      const myBrands = (myBrandRows ?? []) as MyBrandRow[]

      // Brands this user can work in that belong to no account they're a member
      // of — a SOLO brand that added them on its own Team page (the brand invite
      // writes brand_user_access only, never account_members). Account resolution
      // only ever considers account_members rows, so without surfacing these
      // separately such a brand is unreachable for anyone who also belongs to an
      // agency: the lobby lists only the agency's brands, and BrandSwitcher is
      // hidden for agency accounts.
      //
      // Only meaningful for someone who ALSO belongs to an account. With no
      // account memberships at all there is nothing to escape from: the solo
      // fallback below already resolves their brand, and BrandSwitcher already
      // handles having several. Listing them here too would show the same brand
      // twice — once as the derived solo account, once as a standalone brand.
      const standalone = memberIds.length
        ? myBrands.filter(b => !b.account_id || !memberIds.includes(b.account_id))
        : []
      setStandaloneBrands(standalone.map(agencyBrandFromConfig))

      // The user explicitly picked one of those standalone brands. Resolve the
      // SOLO context for it and skip account resolution entirely — this is the
      // same path a brand-scoped team member (no account_members row at all)
      // already takes, so nothing new is being invented here.
      //
      // Every condition is required: an agency checkout return must never be
      // redirected mid-activation; the anchor must already BE that brand
      // (switchBrandWorkspace sets ACTIVE_BRAND_KEY so useBrandLoader resolves
      // it); and the brand must still be standalone-accessible, so a stale
      // choice — access revoked, or the brand absorbed into an agency the user
      // joined — falls through to normal resolution instead of stranding them.
      const ws = readWorkspaceChoice()
      if (
        ws?.kind === 'brand' &&
        !isAgencyCheckoutReturn() &&
        brand && brand.id === ws.id &&
        standalone.some(b => b.id === ws.id)
      ) {
        // Ownership is already in accessRows — this used to be a third read of
        // brand_user_access for a row we had just fetched.
        const ownRow = findAccess(accessRows, brand.id)
        setData(soloAccountFromBrand(brand, ownRow?.is_owner ? userId : ''))
        setActiveWorkspace({ kind: 'brand', id: brand.id })
        setMyAccountRole(null)   // no account membership backs a brand workspace
        setLoading(false)
        return
      }

      let acctRow: any = memberIds.length ? pickAccount(acctRows, brand) : null
      // Brand-scoped team member (no account_members row) or pre-backfill: fall
      // back to the anchor brand's account. RLS hides an agency accounts row from
      // a non-member, so this stays null for them → solo fallback below.
      if (!acctRow && brand?.account_id) {
        const { data: single } = await supabase
          .from('accounts').select('*').eq('id', brand.account_id).maybeSingle()
        if (cancelled) return
        acctRow = single ?? null
      }

      if (acctRow) {
        const accountId = acctRow.id
        // Already scoped to this user's own access by the hoisted read above.
        const brands = myBrands
          .filter(b => b.account_id === accountId)
          .map(agencyBrandFromConfig)
        // Guarantee the anchor brand is present even if a sibling was hidden by
        // RLS — but not if it's been archived (is_active === false).
        //
        // `brand.account_id === accountId` is load-bearing, not a formality. The
        // anchor comes from useBrandLoader, which picks from ALL of the user's
        // brand_user_access rows with no notion of accounts. For someone who
        // belongs to TWO agencies, the anchor is frequently a brand from the
        // OTHER one — and unshifting it here listed a foreign agency's brand in
        // this agency's lobby, under this agency's name.
        if (
          brand &&
          brand.account_id === accountId &&
          brand.is_active !== false &&
          !brands.some(b => b.id === brand.id)
        ) {
          brands.unshift(agencyBrandFromConfig(brand))
        }
        // The caller's role ON THE RESOLVED ACCOUNT — not the first membership
        // row, which may belong to a stray account pickAccount discarded.
        const myRole = memberships.find(m => m.account_id === accountId)?.role ?? null
        setData({ account: accountFromRow(acctRow), brands })
        setActiveWorkspace({ kind: 'account', id: accountId })
        setMyAccountRole((myRole as AccountRole | null) ?? null)
        setLoading(false)
        return
      }
      // No readable account (brand-scoped member, or a legacy brand that never
      // got an account_id) → solo account derived from the brand. Resolve real
      // ownership from brand_user_access so owner-gated actions — notably the
      // sidebar "Upgrade to Agency" — show for legacy brands the user owns. The
      // derived account has no owner_user_id of its own; brand-scoped team members
      // (is_owner=false) stay non-owners.
      // Ownership comes from the accessRows already in hand — this was another
      // read of brand_user_access for a row fetched at the top of this effect.
      const soloOwnerId = brand && findAccess(accessRows, brand.id)?.is_owner ? userId : ''
      // Solo fallback: no account membership at all, so no account role. An
      // owner of a legacy brand still gets owner-gated actions via isAccountOwner
      // (derived from brand_user_access.is_owner in soloAccountFromBrand).
      const solo = brand ? soloAccountFromBrand(brand, soloOwnerId) : null
      setData(solo)
      setAllAccounts(solo ? [solo.account] : [])
      setActiveWorkspace(solo ? { kind: 'account', id: solo.account.id } : null)
      setMyAccountRole(null)
      setLoading(false)
    })()
    return () => { cancelled = true }
    // userId + brand identity are the signals; reloadKey forces a refetch.
    // brandLoading must stay a dep too — it's what re-fires this effect once
    // the brand loader settles (brand?.id/account_id may not change at all if
    // it settles to null, e.g. a genuinely brand-less user).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, brand?.id, brand?.account_id, brandLoading, reloadKey])

  const [activeBrandId, setActiveBrandId] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_KEY) } catch { return null }
  })

  // Default the active brand to the first one if unset or stale.
  useEffect(() => {
    if (!data) return
    if (!activeBrandId || !data.brands.some(b => b.id === activeBrandId)) {
      setActiveBrandId(data.brands[0]?.id ?? null)
    }
  }, [data, activeBrandId])

  const setActiveBrand = useCallback((id: string) => {
    setActiveBrandId(id)
    try { localStorage.setItem(ACTIVE_KEY, id) } catch { /* storage disabled */ }
  }, [])

  // "Inside a brand" vs "at the lobby". Persisted for the session so a refresh
  // while working in a brand keeps you there (a fresh login starts at the lobby).
  const [inBrand, setInBrandState] = useState<boolean>(() => {
    try { return sessionStorage.getItem(IN_BRAND_KEY) === '1' } catch { return false }
  })
  const setInBrand = useCallback((v: boolean) => {
    setInBrandState(v)
    try { v ? sessionStorage.setItem(IN_BRAND_KEY, '1') : sessionStorage.removeItem(IN_BRAND_KEY) } catch { /* storage disabled */ }
  }, [])
  const switchAccount = useCallback((accountId: string) => {
    try { localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId) } catch { /* storage disabled */ }
    // The old account's brand selection is meaningless in the new one — worse,
    // keeping it would resolve a brand the user may not even be in. Clear it and
    // hard-reload to the lobby so nothing from the previous account survives.
    clearActiveBrandSelection()
    window.location.replace('/agency')
  }, [])

  /** Enter a standalone brand as its own workspace. Sets BOTH keys on purpose:
   *  the `brand:` marker tells the loader to take the solo path, and
   *  ACTIVE_BRAND_KEY makes useBrandLoader resolve that brand as the anchor —
   *  the solo path reads the anchor, so they have to agree. */
  const switchBrandWorkspace = useCallback((brandId: string) => {
    try {
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, `brand:${brandId}`)
      localStorage.setItem(ACTIVE_BRAND_KEY, brandId)
      localStorage.setItem(ACTIVE_KEY, brandId)
      sessionStorage.removeItem(IN_BRAND_KEY)   // no agency lobby in a brand workspace
    } catch { /* storage disabled */ }
    // Hard reload so nothing from the previous workspace survives, straight to
    // the brand dashboard (a brand workspace has no lobby to land on).
    window.location.replace('/')
  }, [])

  /**
   * Enter a brand, but only if the access is still there.
   *
   * The lobby's card list is a snapshot from page load. An agency admin can
   * revoke a brand while that tab sits open, and entering a brand you no longer
   * have gives you a dashboard where every single request 403s — which reads as
   * "this app is broken", not "your access was removed". So this asks the
   * database at the moment of the click, which is the only answer that can't be
   * stale.
   *
   * Fails OPEN on a request error: a flaky network must not lock someone out of
   * a brand they still have. It fails closed only on a definitive "no such row"
   * (maybeSingle returns data null with error null), which is what a revoke
   * actually looks like.
   *
   * Returns false when entry was refused, so the caller doesn't navigate.
   */
  const enterBrand = useCallback(async (id: string): Promise<boolean> => {
    if (userId) {
      const { data: row, error } = await supabase
        .from('brand_user_access').select('brand_id')
        .eq('user_id', userId).eq('brand_id', id).maybeSingle()
      if (!error && !row) {
        setAccessRevoked({ id, name: brandNameRef.current.get(id) ?? 'That brand' })
        refreshAccount()   // drop the card, and the switcher entry, without a reload
        return false
      }
    }
    setActiveBrand(id)
    setInBrand(true)
    return true
  }, [userId, setActiveBrand, setInBrand, refreshAccount])
  const exitToAgency = useCallback(() => { setInBrand(false) }, [setInBrand])

  // Reconcile inBrand with the ACTUAL URL on every route change — not just the
  // explicit enterBrand/exitToAgency click handlers. Those handlers set the flag
  // and navigate() together, but browser back/forward (and manually typing a URL)
  // change the route via popstate without ever running them, which used to leave
  // the sessionStorage flag stuck from whichever page was visited last. That
  // desync showed up as: back-navigate from a brand to /agency, and the sidebar
  // (which reads inBrand, not the path) kept rendering brand chrome over the
  // agency lobby. Bare "/" is deliberately left alone — it's ambiguous on its
  // own (DefaultRedirect decides using the inBrand value that's already there).
  const location = useLocation()
  useEffect(() => {
    const path = location.pathname
    const atAgencyRoute = path === '/agency' || path.startsWith('/agency/')
    if (atAgencyRoute) setInBrand(false)
    else if (path !== '/') setInBrand(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Keep the name map current, and re-check access when the tab comes back:
  // the roster in front of someone who left a tab open for an hour is an hour
  // old, and this is the cheap way to make it not be.
  useEffect(() => {
    for (const b of data?.brands ?? []) brandNameRef.current.set(b.id, b.brand_name)
  }, [data])

  // Account resolution is four to five reads, so it goes through the shared,
  // coalesced focus source rather than its own pair of listeners. It previously
  // subscribed to BOTH 'focus' and 'visibilitychange', which fire back to back
  // on a tab return — the hand-rolled 15s throttle was the only thing stopping
  // it running twice each time. lib/revalidate.ts coalesces that properly.
  useEffect(() => {
    if (!userId) return
    return onAppRevalidate(refreshAccount, ACCOUNT_REVALIDATE_MS)
  }, [userId, refreshAccount])

  return useMemo(() => ({
    account: data?.account ?? null,
    brands: data?.brands ?? [],
    isAgency: data?.account.type === 'agency',
    isAccountOwner: !!data?.account && !!userId && data.account.owner_user_id === userId,
    accounts: allAccounts,
    switchAccount,
    standaloneBrands,
    switchBrandWorkspace,
    activeWorkspace,
    accountRole: myAccountRole,
    // The owner always manages, even if their account_members row is missing or
    // says something else — ownership on the accounts row is the stronger claim.
    isAccountAdmin:
      myAccountRole === 'owner' || myAccountRole === 'co_owner' || myAccountRole === 'admin' ||
      (!!data?.account && !!userId && data.account.owner_user_id === userId),
    canViewAgency:
      myAccountRole === 'owner' || myAccountRole === 'co_owner' || myAccountRole === 'admin' ||
      myAccountRole === 'viewer' ||
      (!!data?.account && !!userId && data.account.owner_user_id === userId),
    canManageAccount:
      myAccountRole === 'owner' || myAccountRole === 'co_owner' ||
      (!!data?.account && !!userId && data.account.owner_user_id === userId),
    activeBrandId,
    setActiveBrand,
    inBrand,
    enterBrand,
    exitToAgency,
    refreshAccount,
    accessRevoked,
    noteAccessRevoked: setAccessRevoked,
    clearAccessRevoked,
    loading,
  }), [data, allAccounts, switchAccount, standaloneBrands, switchBrandWorkspace, activeWorkspace,
       accessRevoked, clearAccessRevoked,
       myAccountRole, userId, activeBrandId, setActiveBrand, inBrand, enterBrand, exitToAgency,
       refreshAccount, loading])
}
